/*
 * Checkbox and Switch. Not in Figma (DESIGN-SPEC lists the checkbox as missing); kept to the
 * accent and the field focus ring. Checkbox: a native box (16px, accent-color --accent-default)
 * with its label 400 14/20 beside it. Switch: a button with role="switch", track 32×18 radius 9,
 * --accent-default when on, --border-strong when off, a 14px white thumb; label beside it.
 * Both take taps 40px high on a touch screen.
 */
import { useId } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'
import { cx } from './cx'

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'id'> {
    label: ReactNode
    /** Keep the label for screen readers only (a table's row checkbox). */
    hideLabel?: boolean
}

export function Checkbox({ label, hideLabel, className, ...rest }: CheckboxProps) {
    const id = useId()
    return (
        <span className={cx('inline-flex items-center gap-[8px] pointer-coarse:min-h-[var(--size-touch-target)]', className)}>
            <input
                id={id}
                type="checkbox"
                className="m-0 size-[16px] shrink-0 cursor-pointer accent-[var(--accent-default)] focus-visible:shadow-[var(--focus-m)] focus-visible:outline-none disabled:cursor-not-allowed"
                {...rest}
            />
            <label htmlFor={id} className={cx('cursor-pointer text-[14px] leading-[20px] font-normal text-[var(--text-default)]', hideLabel && 'sr-only')}>
                {label}
            </label>
        </span>
    )
}

export function Switch({
    checked,
    onChange,
    label,
    disabled,
    className,
    testId,
}: {
    checked: boolean
    onChange: (checked: boolean) => void
    label: ReactNode
    disabled?: boolean
    className?: string
    testId?: string
}) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            disabled={disabled}
            data-testid={testId}
            onClick={() => onChange(!checked)}
            className={cx(
                'inline-flex cursor-pointer items-center gap-[8px] rounded-[6px] border-0 bg-transparent p-0 text-left focus-visible:shadow-[var(--focus-m)] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 pointer-coarse:min-h-[var(--size-touch-target)]',
                className,
            )}
        >
            <span
                aria-hidden
                className={cx('relative h-[18px] w-[32px] shrink-0 rounded-[9px] transition-colors', checked ? 'bg-[var(--accent-default)]' : 'bg-[var(--border-strong)]')}
            >
                <span className={cx('absolute top-[2px] size-[14px] rounded-full bg-white shadow-[0_1px_2px_rgb(16_16_19/0.2)] transition-[left]', checked ? 'left-[16px]' : 'left-[2px]')} />
            </span>
            <span className="text-[14px] leading-[20px] font-normal text-[var(--text-default)]">{label}</span>
        </button>
    )
}
